import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ModalComponent } from "../modal.component";
import { ModalHeaderComponent } from "../modal.header.component";
import { AttachmentService, FileItem } from "../../../services/attachment.service";
import { S25File } from "../../s25-file-upload/s25.file.upload.component";
import { S25Util } from "../../../util/s25-util";
import { FileStoreUtil } from "../../../util/filestore.util";

/** Max filename length aligned with s25-ng-editable-file validation (112). */
export const DUPLICATE_UPLOAD_MAX_FILENAME_LENGTH = 112;

export interface ModalDuplicateFileUploadData {
    duplicateFileName: string;
    conflictingCustomAttributeName?: string;
    incomingFile: S25File;
    conflictingFile: FileItem;
    objectTypeId: number;
    objectId: number;
    fileKey?: string;
    suggestedFileName: string;
    existingFileNames: ReadonlySet<string>;
    onReplace: () => Promise<void>;
    onUploadWithNewName: (file: S25File) => Promise<void>;
}

const imageExtensionSet = new Set(FileStoreUtil.IMAGE_EXTENSIONS);

function getFileExtensionForPreview(fileName: string): string | undefined {
    const parts = fileName.split(".");
    if (parts.length < 2) return undefined;
    return S25Util.toStr(parts[parts.length - 1]).toLowerCase();
}

function formatByteSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

@Component({
    selector: "s25-modal-duplicate-file-upload",
    standalone: true,
    imports: [CommonModule, FormsModule, ModalHeaderComponent],
    template: `
        @if (params()) {
            <div class="s25-ng">
                <s25-modal-header [data]="data"></s25-modal-header>
                <div class="modal-body duplicate-file-upload-modal">
                    <p>
                        <strong>{{ data.duplicateFileName }}</strong> is already used for an attachment on this object.
                        File names must be unique per object.
                    </p>
                    @if (data.conflictingCustomAttributeName) {
                        <p>
                            It is already used by the custom attribute
                            <strong>{{ data.conflictingCustomAttributeName }}</strong
                            >.
                        </p>
                    }
                    <div class="duplicate-file-upload-modal__previews">
                        <div class="duplicate-file-upload-modal__preview-col">
                            <div class="duplicate-file-upload-modal__preview-title">File you are uploading</div>
                            @if (incomingIsImage() && incomingObjectUrl()) {
                                <img
                                    class="duplicate-file-upload-modal__thumb"
                                    [src]="incomingObjectUrl()!"
                                    alt="Preview of file you are uploading"
                                />
                            } @else {
                                <div class="duplicate-file-upload-modal__file-meta">
                                    {{ data.incomingFile.name }} ({{ incomingSizeLabel() }})
                                </div>
                            }
                        </div>
                        <div class="duplicate-file-upload-modal__preview-col">
                            <div class="duplicate-file-upload-modal__preview-title">Existing file</div>
                            @if (existingIsImage() && existingSafeUrl()) {
                                <img
                                    class="duplicate-file-upload-modal__thumb"
                                    [src]="existingSafeUrl()!"
                                    alt="Preview of existing file with the same name"
                                />
                            } @else {
                                <div class="duplicate-file-upload-modal__file-meta">{{ data.duplicateFileName }}</div>
                            }
                        </div>
                    </div>
                    <p class="duplicate-file-upload-modal__warning">
                        Replacing removes the existing attachment for that file name. Any custom attribute or field that
                        pointed to it may break until updated.
                    </p>
                    <label class="duplicate-file-upload-modal__rename-label">
                        Upload with a different file name instead:
                        <input
                            type="text"
                            class="cn-form__control c-margin-top--quarter"
                            name="alternateFileName"
                            [ngModel]="alternateFileName()"
                            (ngModelChange)="alternateFileName.set($event)"
                            [maxlength]="maxFilenameLength"
                        />
                    </label>
                    @if (renameError()) {
                        <div class="ngRed c-margin-top--quarter">{{ renameError() }}</div>
                    }
                </div>
                <div class="modal-footer duplicate-file-upload-modal__footer">
                    <button type="button" class="aw-button aw-button--outline" (click)="cancel()">Cancel</button>
                    <button
                        type="button"
                        class="aw-button aw-button--outline aw-button--primary"
                        (click)="uploadWithNewName()"
                        [disabled]="busy()"
                    >
                        Upload with new name
                    </button>
                    <button type="button" class="aw-button aw-button--outline" (click)="replace()" [disabled]="busy()">
                        Replace existing file
                    </button>
                </div>
            </div>
        }
    `,
    styles: `
        .duplicate-file-upload-modal__previews {
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
            margin: 1rem 0;
        }
        .duplicate-file-upload-modal__preview-col {
            flex: 1 1 12rem;
            min-width: 10rem;
        }
        .duplicate-file-upload-modal__preview-title {
            font-weight: 600;
            margin-bottom: 0.5rem;
        }
        .duplicate-file-upload-modal__thumb {
            max-width: 100%;
            max-height: 14rem;
            object-fit: contain;
            border: 1px solid #ddd;
        }
        .duplicate-file-upload-modal__file-meta {
            word-break: break-word;
        }
        .duplicate-file-upload-modal__warning {
            font-size: 0.9rem;
            color: #555;
        }
        .duplicate-file-upload-modal__rename-label {
            display: block;
            margin-top: 1rem;
        }
        .duplicate-file-upload-modal__footer {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            justify-content: flex-end;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalDuplicateFileUploadComponent extends ModalComponent<ModalDuplicateFileUploadData> {
    static title = "File name already in use";
    static size: "sm" | "md" | "lg" | "xl" | "xxl" = "lg";
    static asDialog = true;

    readonly maxFilenameLength = DUPLICATE_UPLOAD_MAX_FILENAME_LENGTH;

    private readonly sanitizer = inject(DomSanitizer);

    readonly alternateFileName = signal("");
    readonly renameError = signal("");
    readonly busy = signal(false);
    readonly incomingObjectUrl = signal<string | undefined>(undefined);

    readonly incomingIsImage = computed(() => {
        const fileRef = this.params()?.incomingFile?.fileRef;
        return !!fileRef?.type?.startsWith("image/");
    });

    readonly incomingSizeLabel = computed(() => {
        const bytes = this.params()?.incomingFile?.fileRef?.size;
        if (bytes == null) return "";
        return formatByteSize(bytes);
    });

    readonly existingIsImage = computed(() => {
        const fileName = this.params()?.duplicateFileName;
        if (!fileName) return false;
        const extension = getFileExtensionForPreview(fileName);
        return !!extension && imageExtensionSet.has(extension);
    });

    readonly existingSafeUrl = computed((): SafeResourceUrl | undefined => {
        if (!this.existingIsImage()) return undefined;
        const payload = this.params();
        if (!payload) return undefined;
        const rawUrl = AttachmentService.getFileUrl(
            payload.objectTypeId,
            payload.objectId,
            payload.duplicateFileName,
            "inline",
            payload.fileKey,
            payload.conflictingFile.custAtrbId,
        );
        return this.sanitizer.bypassSecurityTrustResourceUrl(rawUrl);
    });

    private readonly syncSuggestedFilename = effect(() => {
        const payload = this.params();
        if (!payload) return;
        this.alternateFileName.set(payload.suggestedFileName);
    });

    private readonly incomingObjectUrlEffect = effect((onCleanup) => {
        const payload = this.params();
        const fileRef = payload?.incomingFile?.fileRef;
        if (!fileRef) {
            this.incomingObjectUrl.set(undefined);
            return;
        }
        const objectUrl = URL.createObjectURL(fileRef);
        this.incomingObjectUrl.set(objectUrl);
        onCleanup(() => URL.revokeObjectURL(objectUrl));
    });

    cancel() {
        this.data.closeModal();
    }

    async replace() {
        this.renameError.set("");
        this.busy.set(true);
        try {
            await this.data.onReplace();
            this.data.closeModal();
        } finally {
            this.busy.set(false);
        }
    }

    async uploadWithNewName() {
        this.renameError.set("");
        const trimmed = this.alternateFileName().trim();
        if (!trimmed) {
            this.renameError.set("File name cannot be empty.");
            return;
        }
        if (trimmed.length > DUPLICATE_UPLOAD_MAX_FILENAME_LENGTH) {
            this.renameError.set(
                `The file name is too long. File names can be no longer than ${DUPLICATE_UPLOAD_MAX_FILENAME_LENGTH} characters.`,
            );
            return;
        }
        const incomingRef = this.data.incomingFile.fileRef;
        if (!incomingRef) {
            this.renameError.set("The file data is missing. Try choosing the file again.");
            return;
        }
        const sanitized = AttachmentService.sanitizeFilenameSpaces(trimmed) ?? trimmed;
        if (this.data.existingFileNames.has(sanitized)) {
            this.renameError.set("That file name is already in use. Choose a different name.");
            return;
        }
        const renamed = new File([incomingRef], sanitized, { type: incomingRef.type });
        const renamedS25: S25File = {
            name: renamed.name,
            size: renamed.size,
            isChosen: true,
            fileRef: renamed,
            data: this.data.incomingFile.data,
        };
        this.busy.set(true);
        try {
            await this.data.onUploadWithNewName(renamedS25);
            this.data.closeModal();
        } catch {
            // Parent handles errors; keep modal open
        } finally {
            this.busy.set(false);
        }
    }
}

export function suggestUniqueFilename(originalName: string, existingNames: ReadonlySet<string>): string {
    const trimmed = originalName?.trim() ?? "";
    if (!trimmed) return trimmed;
    const dot = trimmed.lastIndexOf(".");
    const base = dot > 0 ? trimmed.slice(0, dot) : trimmed;
    const ext = dot > 0 ? trimmed.slice(dot) : "";
    let counter = 2;
    let candidate = `${base} (${counter})${ext}`;
    while (existingNames.has(candidate) && counter < 5000) {
        counter++;
        candidate = `${base} (${counter})${ext}`;
    }
    if (candidate.length <= DUPLICATE_UPLOAD_MAX_FILENAME_LENGTH) {
        return candidate;
    }
    const suffix = ` (${counter})${ext}`;
    const maxBaseLen = DUPLICATE_UPLOAD_MAX_FILENAME_LENGTH - suffix.length;
    const truncatedBase =
        maxBaseLen > 0 ? base.slice(0, maxBaseLen) : base.slice(0, DUPLICATE_UPLOAD_MAX_FILENAME_LENGTH);
    return `${truncatedBase}${suffix}`.slice(0, DUPLICATE_UPLOAD_MAX_FILENAME_LENGTH);
}
